// Admin UI strings. EN + RU per §3 v1; FR/ES/ZH/AR slots fall back to EN.
// Concept §7 verbatim text for the pre-publication checklist lives here.

export type AdminLang = "en" | "ru" | "fr" | "es" | "zh" | "ar";

export type AdminDict = {
  sign_in: string; sign_in_lead: string; email: string; password: string;
  sign_in_btn: string; sign_out: string; signed_in_as: string; no_role: string;
  ui_locale: string;
  queue: string; queue_empty: string; queue_oldest: string;
  col_title: string; col_language: string; col_type: string; col_submitted: string;
  col_status: string; col_contact: string;
  type_victim: string; type_confessor: string;
  status_submitted: string; status_in_review: string; status_needs_redaction: string;
  back_to_queue: string;
  original_header: string; original_lang: string;
  edited_header: string; title_label: string;
  reason_label: string; reason_placeholder: string;
  save_edit: string; saved: string;
  publish: string; reject: string; needs_redaction: string; clarify: string;
  publish_disabled_hint: string;
  reject_prompt: string; needs_redaction_prompt: string; clarify_prompt: string;
  no_contact_disabled: string;
  facets: string; categories_label: string; warnings_label: string;
  contact_yes: string; contact_no: string;
  redaction_log: string; redaction_empty: string;
  triage_title: string; triage_body: string;
  checklist_title: string; checklist_hint: string;
  rule_1: string; rule_2: string; rule_3: string; rule_4: string; rule_5: string;
  rule_6: string; rule_6_a: string; rule_6_b: string;
  rule_7: string;
  privacy_note: string;
  translations_title: string;
  translations_empty: string;
  translations_none_review: string;
  translations_approve: string;
  translations_reject: string;
  translations_approve_all: string;
  translations_reject_prompt: string;
  translations_show_body: string;
  translations_hide_body: string;
  tab_queue: string;
  tab_published: string;
  published_empty: string;
  published_lead: string;
  col_published: string;
  status_published: string;
  // Facets editor (Task 1)
  facets_edit_hint: string;
  save_facets: string;
  facets_saved: string;
  content_warnings_placeholder: string;
  content_warnings_hint: string;
  categories_loading: string;
  // Translation review (Task 2)
  translation_original: string;
  translation_edited: string;
  translation_reason_label: string;
  translation_reason_placeholder: string;
  save_translation: string;
  translation_saved: string;
};

const en: AdminDict = {
  sign_in: "Sign in",
  sign_in_lead: "Staff access only. Accounts created by the owner.",
  email: "Email", password: "Password",
  sign_in_btn: "Sign in", sign_out: "Sign out",
  signed_in_as: "Signed in as",
  no_role: "Your account has no staff role. Ask the owner to assign one.",
  ui_locale: "Admin language",
  queue: "Moderation queue",
  queue_empty: "Queue is empty. No submissions waiting.",
  queue_oldest: "Oldest first.",
  col_title: "Title", col_language: "Lang", col_type: "Type",
  col_submitted: "Submitted", col_status: "Status", col_contact: "Contact",
  type_victim: "victim", type_confessor: "confessor",
  status_submitted: "submitted", status_in_review: "in review",
  status_needs_redaction: "needs redaction",
  back_to_queue: "← Back to queue",
  original_header: "Original (submissions_raw — read-only)",
  original_lang: "Original language",
  edited_header: "Redacted (editable)",
  title_label: "Title",
  reason_label: "Reason for this edit (required, appended to redaction_log)",
  reason_placeholder: "e.g. masked vessel name; removed two crew names; replaced port + date pair",
  save_edit: "Save edit", saved: "Saved.",
  publish: "Publish", reject: "Reject",
  needs_redaction: "Needs redaction",
  clarify: "Request clarification",
  publish_disabled_hint:
    "Publish becomes available after every item in the §7 checklist is ticked.",
  reject_prompt: "Reason for rejection (visible only to staff):",
  needs_redaction_prompt: "What still needs to be redacted?",
  clarify_prompt: "What clarification do you need from the author?",
  no_contact_disabled: "Author left no contact — clarification cannot be sent.",
  facets: "Facets", categories_label: "Categories", warnings_label: "Content warnings",
  contact_yes: "contact: yes", contact_no: "contact: none",
  redaction_log: "Redaction log",
  redaction_empty: "No edits yet.",
  triage_title: "Triage assistant (§15.2)",
  triage_body:
    "AI-assist pipelines are not yet wired. The moderator does all work by hand. Stub UI only.",
  checklist_title: "§7 pre-publication checklist",
  checklist_hint: "Tick every item — Publish stays locked until then.",
  rule_1:
    "1. No vessel names, IMO numbers, company names, ports + dates that combine into identification.",
  rule_2: "2. No real personal names of accused or third parties.",
  rule_3: "3. Content warnings assigned where needed.",
  rule_4: "4. Category and facets sanity-checked.",
  rule_5: "5. Tone: testimony, not call to violence or doxxing — those are rejected.",
  rule_6:
    "6. Do NOT edit for language register. Strong language, profanity, and coarse vocabulary are part of the testimony and must not be sanitized. Edit only what identifies people, vessels, or companies.",
  rule_6_a:
    "Exception A — calls for violence, doxxing, threats: replace the passage with [xxx] (visibly). The original is kept in submissions_raw.",
  rule_6_b:
    "Exception B — speech quoted from the perpetrator (slurs, profanity, degrading language) is testimony: preserved verbatim in the original language; in translations, the slur is translated accurately in parentheses after the original word.",
  rule_7:
    "7. Confessor submissions: verify no victims are identifiable. The confessor's anonymity is protected as rigorously as any victim's.",
  privacy_note:
    "author_contact is admin-only and never appears in this UI beyond a yes/no flag. It is excluded from every AI payload.",
  translations_title: "Translations",
  translations_empty: "No translations yet.",
  translations_none_review: "No translations awaiting review.",
  translations_approve: "Approve",
  translations_reject: "Reject",
  translations_approve_all: "Approve all in review",
  translations_reject_prompt: "Reason for rejecting this translation:",
  translations_show_body: "Show body",
  translations_hide_body: "Hide body",
  tab_queue: "In moderation",
  tab_published: "Published originals",
  published_empty: "No published originals yet.",
  published_lead: "Newest first. Open to review translations.",
  col_published: "Published",
  status_published: "published",
  facets_edit_hint: "Tick categories that apply; edit content warnings as needed.",
  save_facets: "Save facets",
  facets_saved: "Facets saved.",
  content_warnings_placeholder: "e.g. suicide, sexual assault, self-harm",
  content_warnings_hint: "Comma-separated. Lowercase, short.",
  categories_loading: "Loading categories…",
  translation_original: "Original",
  translation_edited: "Translation (editable)",
  translation_reason_label: "Reason for this edit (required, appended to redaction_log)",
  translation_reason_placeholder: "e.g. fixed rank translation; smoothed idiom",
  save_translation: "Save translation",
  translation_saved: "Translation saved.",
};

const ru: AdminDict = {
  sign_in: "Вход",
  sign_in_lead: "Только для сотрудников. Учётные записи создаёт владелец.",
  email: "Эл. почта", password: "Пароль",
  sign_in_btn: "Войти", sign_out: "Выйти",
  signed_in_as: "Вошли как",
  no_role: "У учётной записи нет роли. Попросите владельца назначить роль.",
  ui_locale: "Язык админки",
  queue: "Очередь модерации",
  queue_empty: "Очередь пуста.",
  queue_oldest: "Сначала самые старые.",
  col_title: "Заголовок", col_language: "Язык", col_type: "Тип",
  col_submitted: "Получено", col_status: "Статус", col_contact: "Контакт",
  type_victim: "пострадавший", type_confessor: "исповедь",
  status_submitted: "новый", status_in_review: "в работе",
  status_needs_redaction: "нужна редактура",
  back_to_queue: "← В очередь",
  original_header: "Оригинал (submissions_raw — только чтение)",
  original_lang: "Язык оригинала",
  edited_header: "Отредактированная версия",
  title_label: "Заголовок",
  reason_label: "Причина правки (обязательно, попадает в redaction_log)",
  reason_placeholder:
    "напр.: скрыто имя судна; убраны два имени экипажа; заменена пара порт + дата",
  save_edit: "Сохранить правку", saved: "Сохранено.",
  publish: "Опубликовать", reject: "Отклонить",
  needs_redaction: "Нужна редактура",
  clarify: "Запросить уточнение",
  publish_disabled_hint:
    "Кнопка «Опубликовать» активна после отметки всех пунктов чек-листа §7.",
  reject_prompt: "Причина отклонения (видна только сотрудникам):",
  needs_redaction_prompt: "Что ещё нужно отредактировать?",
  clarify_prompt: "Какое уточнение запросить у автора?",
  no_contact_disabled: "Автор не оставил контакта — запрос невозможен.",
  facets: "Фасеты", categories_label: "Категории", warnings_label: "Предупреждения",
  contact_yes: "контакт: есть", contact_no: "контакт: нет",
  redaction_log: "Журнал редактуры",
  redaction_empty: "Правок ещё не было.",
  triage_title: "Помощник триажа (§15.2)",
  triage_body:
    "ИИ-конвейеры пока не подключены. Модератор делает всё вручную. Только заглушка.",
  checklist_title: "Чек-лист §7 перед публикацией",
  checklist_hint: "Отметьте все пункты — «Опубликовать» разблокируется после этого.",
  rule_1:
    "1. Никаких названий судов, номеров ИМО, названий компаний, портов и дат, которые в сумме идентифицируют.",
  rule_2: "2. Никаких реальных имён обвиняемых или третьих лиц.",
  rule_3: "3. Расставлены предупреждения о содержании, где необходимо.",
  rule_4: "4. Категория и фасеты проверены здравым смыслом.",
  rule_5:
    "5. Тон: свидетельство, не призыв к насилию и не доксинг — такие истории отклоняются.",
  rule_6:
    "6. НЕ редактируйте регистр языка. Крепкие слова, мат, грубая лексика — часть свидетельства, их нельзя «обелять». Редактируется только то, что идентифицирует людей, суда, компании.",
  rule_6_a:
    "Исключение A — призывы к насилию, доксинг, угрозы: заменить фрагмент на [xxx] (видимо). Оригинал сохраняется в submissions_raw.",
  rule_6_b:
    "Исключение B — речь, цитируемая от обидчика (оскорбления, мат, унижение): свидетельство, сохраняется дословно на языке оригинала; в переводах оскорбление точно переводится в скобках после оригинального слова.",
  rule_7:
    "7. Истории-исповеди: убедитесь, что нельзя опознать жертв. Анонимность исповедующегося защищается так же строго, как анонимность пострадавшего.",
  privacy_note:
    "author_contact доступен только администраторам и не появляется в UI кроме флага «есть/нет». Он исключён из любых ИИ-запросов.",
  translations_title: "Переводы",
  translations_empty: "Переводов пока нет.",
  translations_none_review: "Нет переводов, ожидающих проверки.",
  translations_approve: "Одобрить",
  translations_reject: "Отклонить",
  translations_approve_all: "Одобрить все на проверке",
  translations_reject_prompt: "Причина отклонения перевода:",
  translations_show_body: "Показать текст",
  translations_hide_body: "Скрыть текст",
  tab_queue: "На модерации",
  tab_published: "Опубликованные оригиналы",
  published_empty: "Опубликованных оригиналов пока нет.",
  published_lead: "Сначала новые. Откройте, чтобы проверить переводы.",
  col_published: "Опубликовано",
  status_published: "опубликовано",
  facets_edit_hint: "Отметьте подходящие категории; при необходимости отредактируйте предупреждения.",
  save_facets: "Сохранить фасеты",
  facets_saved: "Фасеты сохранены.",
  content_warnings_placeholder: "напр.: суицид, сексуальное насилие, самоповреждение",
  content_warnings_hint: "Через запятую. Строчными буквами, коротко.",
  categories_loading: "Загрузка категорий…",
  translation_original: "Оригинал",
  translation_edited: "Перевод (редактируемый)",
  translation_reason_label: "Причина правки (обязательно, попадает в redaction_log)",
  translation_reason_placeholder: "напр.: исправлен перевод должности; сглажена идиома",
  save_translation: "Сохранить перевод",
  translation_saved: "Перевод сохранён.",
};

const dicts: Record<AdminLang, AdminDict> = {
  en, ru, fr: en, es: en, zh: en, ar: en,
};

export function adminStrings(lang: string): AdminDict {
  return dicts[(lang as AdminLang) in dicts ? (lang as AdminLang) : "en"];
}

export const ADMIN_LOCALE_OPTIONS: { code: AdminLang; label: string; ready: boolean }[] = [
  { code: "en", label: "English", ready: true },
  { code: "ru", label: "Русский", ready: true },
  { code: "fr", label: "Français", ready: false },
  { code: "es", label: "Español", ready: false },
  { code: "zh", label: "中文", ready: false },
  { code: "ar", label: "العربية", ready: false },
];